/**
 * Fireworks — multi-burst firework effect for streak milestones
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { kidConfettiColors } from '../../styles/kidPalette';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface FireworksProps {
  trigger: boolean;
  bursts?: number;
  duration?: number;
}

interface Burst {
  id: number;
  x: number;
  y: number;
  delay: number;
  particles: { angle: number; distance: number; color: string; size: number }[];
}

export default function Fireworks({
  trigger,
  bursts: burstCount = 4,
  duration = 2500,
}: FireworksProps) {
  const [bursts, setBursts] = useState<Burst[]>([]);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!trigger || reduced) {
      setBursts([]);
      return;
    }

    const newBursts: Burst[] = Array.from({ length: burstCount }, (_, i) => {
      const particleCount = 20 + Math.floor(Math.random() * 6);
      return {
        id: i,
        x: 20 + Math.random() * 60, // 20-80% viewport width
        y: 20 + Math.random() * 20, // 20-40% viewport height (upper area)
        delay: (i / burstCount) * (duration * 0.4),
        particles: Array.from({ length: particleCount }, (_, j) => ({
          angle: (j / particleCount) * Math.PI * 2,
          distance: 50 + Math.random() * 40,
          color: kidConfettiColors[Math.floor(Math.random() * kidConfettiColors.length)],
          size: 3 + Math.random() * 4,
        })),
      };
    });

    setBursts(newBursts);
    const timer = setTimeout(() => setBursts([]), duration);
    return () => clearTimeout(timer);
  }, [trigger, burstCount, duration, reduced]);

  if (reduced) return null;

  return (
    <AnimatePresence>
      {bursts.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {bursts.map((burst) => (
            <div
              key={burst.id}
              className="absolute"
              style={{ left: `${burst.x}%`, top: `${burst.y}%` }}
            >
              {burst.particles.map((p, pi) => {
                const tx = Math.cos(p.angle) * p.distance;
                const ty = Math.sin(p.angle) * p.distance;
                return (
                  <motion.div
                    key={pi}
                    className="absolute rounded-full"
                    style={{
                      width: p.size,
                      height: p.size,
                      backgroundColor: p.color,
                    }}
                    initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
                    animate={{
                      scale: [0, 1, 0.5],
                      x: tx,
                      y: [ty, ty + 30], // gravity curve
                      opacity: [1, 1, 0],
                    }}
                    transition={{
                      duration: 0.8,
                      delay: burst.delay / 1000,
                      ease: 'easeOut',
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}
    </AnimatePresence>
  );
}
